/**
 * The three-column tracking calendar (PRD §6.2) — Gesamt / F&E (Antrag) / F&E (tatsächlich).
 *
 * Prefill, absence chip cycle and week completion follow the reference look; saves go through
 * one week endpoint (PLAN.md D-32). Re-saving a week that already has a saved day requires a
 * change-reason dialog (anySaved — reference behavior; V-12 retro override superseded).
 * Week attachments: optional files on the week description (PLAN.md D-67).
 *
 * Unsaved week drafts (hours + description) live in an in-memory map so month-tab switches
 * keep edits; closing the admin Edit-hours dialog or leaving/refreshing the page drops them.
 * Pre-absence hour snapshots ride that same draft cache so clearing an absence chip restores
 * the values from before the chip was first turned on. A cached week is ignored when the
 * server fingerprint changed (other-project absence / shared Gesamt refetch).
 */

import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Check,
  Download,
  FileSpreadsheet,
  HelpCircle,
  Info,
  Lock,
  Paperclip,
  Palmtree,
  Save,
  Thermometer,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { describeError } from "@/lib/errors";
import { exportEmployeeYearMatrix } from "@/lib/export-xlsx";
import { formatPercent, formatWeekdayDate, formatWeekdayShort } from "@/lib/format";
import {
  useDownloadStundenzettel,
  useLogExport,
  useSaveWeek,
  useWeekAttachments,
  useUploadWeekAttachment,
  useDeleteWeekAttachment,
  useDownloadWeekAttachment,
} from "@/lib/tracking";
import { ApiError } from "@/lib/api";
import {
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_FILES_PER_WEEK,
  allowedExtensionsAccept,
  extensionOf,
  isAllowedExtension,
} from "@/shared/file-types";
import { plannedRd, round1 } from "@/shared/aggregation";
import {
  extraCheckedDayMissingHours,
  isWorkdayRowSavable,
  seedWorkdaySelected,
  showsWorkdayColumn,
  workdayCheckboxLockReason,
  type WorkdayCheckboxLockReason,
} from "@/shared/workday-selection";
import type {
  AbsenceBlockedByOtherProjectHoursPayload,
  AbsenceProjectHours,
  CrossProjectOverbookingPayload,
  ProjectHours,
} from "@/shared/errors";
import { addDays, berlinToday, isoWeekOf, isoWeekday, type BusinessDate } from "@/shared/dates";
import {
  formatHoursInput,
  normalizeHoursInput,
  parseHoursInput,
  rowHasHoursError,
  suppressFocusedUnparseable,
  validateRow,
  type FocusedHoursField,
  type RowHoursValidation,
} from "@/shared/hour-drafts";
import type {
  AbsenceType,
  CalendarDay,
  CalendarMonth,
  WeekDaySaveInput,
} from "@/shared/schemas/tracking";
import type { TFunction } from "i18next";

type AbsenceFraction = 0 | 0.5 | 1;

const OVERBOOK_TOAST_MAX_PROJECTS = 4;

/** Option B breakdown list for overbook / absence-blocked toasts (up to 4 + "+N more"). */
function formatBreakdownToastLines(
  t: TFunction,
  rows: { project_name: string; hours: number }[],
): string {
  const shown = rows.slice(0, OVERBOOK_TOAST_MAX_PROJECTS);
  const lines = shown.map((row) =>
    t("errors.CROSS_PROJECT_OVERBOOKING_line", {
      name: row.project_name,
      hours: row.hours,
    }),
  );
  const extra = rows.length - shown.length;
  if (extra > 0) {
    lines.push(t("errors.CROSS_PROJECT_OVERBOOKING_more", { count: extra }));
  }
  return lines.join("\n");
}

function formatCrossProjectOverbookingMessage(
  t: TFunction,
  payload: CrossProjectOverbookingPayload,
): string {
  const dateLabel = formatWeekdayDate(payload.date);
  const lines = formatBreakdownToastLines(
    t,
    payload.breakdown.map((row: ProjectHours) => ({
      project_name: row.project_name,
      hours: row.rd_hours,
    })),
  );
  return [
    t("errors.CROSS_PROJECT_OVERBOOKING_title", {
      date_label: dateLabel,
      date: payload.date,
    }),
    lines,
    t("errors.CROSS_PROJECT_OVERBOOKING_footer", { total_hours: payload.total_hours }),
  ]
    .filter(Boolean)
    .join("\n");
}

function formatAbsenceBlockedMessage(
  t: TFunction,
  error: ApiError,
  payload: AbsenceBlockedByOtherProjectHoursPayload,
): string {
  const lines = formatBreakdownToastLines(
    t,
    payload.breakdown.map((row: AbsenceProjectHours) => ({
      project_name: row.project_name,
      hours: row.total_hours,
    })),
  );
  return [error.localizedMessage, lines].filter(Boolean).join("\n");
}

function toastMultilineError(message: string, testId: string): void {
  toast.error(
    <div className="whitespace-pre-line" data-testid={testId}>
      {message}
    </div>,
  );
}

/** Hours held before the user first turned an absence chip on for this day (client-only). */
interface PreAbsenceSnapshot {
  totalHours: number;
  rdHours: number;
  totalHoursInput: string;
  rdHoursInput: string;
  rdEntered: boolean;
}

interface DayDraft {
  entryDate: BusinessDate;
  /** Derived numeric for sums / payload — kept in sync with the string drafts. */
  totalHours: number;
  rdHours: number;
  /** Raw input bindings so "", "7.", "0.25" survive while typing. */
  totalHoursInput: string;
  rdHoursInput: string;
  /** True once the user has entered ACTUAL (or the day is already persisted / full absence). */
  rdEntered: boolean;
  /** True once the user edits hours/ACTUAL/absence — prefill alone does not count. */
  userTouched: boolean;
  absenceType: AbsenceType | null;
  absenceFraction: AbsenceFraction;
  /**
   * Snapshot of hours when absence was first switched on this session.
   * Survives month tabs via weekDraftCache; null when absence came from the server / no local on.
   */
  preAbsenceSnapshot: PreAbsenceSnapshot | null;
  /**
   * Full absence recorded on another project — hashed/disabled here; omit from week save.
   * Only when the API exposes a source project (not legacy / not current project).
   */
  absentElsewhere: boolean;
  absenceSourceProjectId: string | null;
  absenceSourceProjectName: string | null;
  /** Other projects that already have a time_entry on this date (Stunden ⓘ). */
  hoursSourceProjects: { id: string; name: string }[];
  /**
   * Ticket 116: user confirmed wiping other projects' hours for a full-day absence.
   * Survives month-tab cache; sent on week save so C5 does not 422.
   */
  clearOtherProjectHours: boolean;
  holiday: string | null;
  isWorkday: boolean;
  isFuture: boolean;
  /** Outside max(project start, assignment entry) … project end. */
  isOutsideWindow: boolean;
  saved: boolean;
  isRetroactive: boolean;
  /**
   * Reduced-days checkbox. Seeded from saved || work_weekdays; whole DayDraft
   * rows ride weekDraftCache so the flag survives month-tab switches.
   */
  isWorkdaySelected: boolean;
  /** Per-day R&D share for F&E (Antrag); from snapshot or history. */
  antragRdShare: number;
}

/** Full absence stamped by another project (half-days stay editable). */
function isAbsentElsewhere(day: CalendarDay): boolean {
  return (
    day.absenceFraction === 1 &&
    day.absenceSourceProjectId !== null &&
    day.absenceSourceProjectId !== ""
  );
}

/** Keep numeric + string hour fields aligned when writing from a known number. */
function hoursFromNumber(
  totalHours: number,
  rdHours: number,
  rdEntered: boolean,
): Pick<DayDraft, "totalHours" | "rdHours" | "totalHoursInput" | "rdHoursInput" | "rdEntered"> {
  return {
    totalHours,
    rdHours,
    totalHoursInput: formatHoursInput(totalHours),
    rdHoursInput: rdEntered ? formatHoursInput(rdHours) : "",
    rdEntered,
  };
}

/** In-memory only — never localStorage / sessionStorage. */
interface WeekDraftSnapshot {
  rows: DayDraft[];
  note: string;
}

/** Cached month-tab draft plus the server fingerprint it was seeded from. */
interface CachedWeekDraft extends WeekDraftSnapshot {
  serverRevision: string;
}

function weekDaysRevision(days: DayDraft[]): string {
  return days
    .map(
      (day) =>
        `${day.entryDate}:${day.totalHours}:${day.rdHours}:${day.absenceType}:${day.absenceFraction}:${day.absenceSourceProjectId}:${day.saved}:${day.hoursSourceProjects.map((project) => project.id).join(",")}`,
    )
    .join("|");
}

function weekDraftKey(userId: string, projectId: string, year: number, isoWeek: number): string {
  // Include userId + projectId so a reused calendar never seeds another person or project.
  return `${userId}:${projectId}:${year}-W${isoWeek}`;
}

/** Employee vs admin destination for hours-source / absent-elsewhere project names. */
function hoursSourceProjectHref(projectId: string, asAdmin: boolean): string {
  return asAdmin ? `/projects/${projectId}` : `/my/tracking/${projectId}`;
}

function HoursSourceProjectLinks({
  projects,
  asAdmin,
}: {
  projects: { id: string; name: string }[];
  asAdmin: boolean;
  children?: ReactNode;
}) {
  return (
    <span>
      {projects.map((project, index) => (
        <span key={project.id}>
          {index > 0 ? ", " : null}
          <a
            href={hoursSourceProjectHref(project.id, asAdmin)}
            className="font-medium underline underline-offset-2"
            data-testid="tracking-hours-source-project-link"
          >
            {project.name}
          </a>
        </span>
      ))}
    </span>
  );
}

/** Click-toggled Stunden ⓘ — project names hard-navigate (full page load). */
function HoursSourceHint({
  projects,
  asAdmin,
}: {
  projects: { id: string; name: string }[];
  asAdmin: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className="inline-flex h-9 w-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-expanded={open}
        aria-label={t("tracker.hoursSharedHintAria")}
        data-testid="tracking-hours-other-project-hint"
        onClick={() => setOpen((prev) => !prev)}
      >
        <Info className="size-3.5" aria-hidden />
      </button>
      {open ? (
        <div
          role="tooltip"
          className="absolute right-0 bottom-full z-50 mb-1 max-w-xs rounded-md bg-primary px-3 py-1.5 text-left text-xs text-primary-foreground shadow-md"
        >
          <Trans
            i18nKey="tracker.hoursSharedTooltipWithProjects"
            components={{
              projects: <HoursSourceProjectLinks projects={projects} asAdmin={asAdmin} />,
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

/** Inclusive tracking window: max(project start, assignment entry) … min(project end, assignment leave). */
function trackingWindowOf(calendar: CalendarMonth): {
  from: BusinessDate;
  to: BusinessDate;
} {
  const from =
    calendar.assignmentEntryDate > calendar.projectStartDate
      ? (calendar.assignmentEntryDate as BusinessDate)
      : (calendar.projectStartDate as BusinessDate);
  const projectEnd = calendar.projectEndDate as BusinessDate;
  const leave = calendar.assignmentLeaveDate as BusinessDate | null;
  const to = leave && leave < projectEnd ? leave : projectEnd;
  return { from, to };
}

function isInTrackingWindow(date: BusinessDate, calendar: CalendarMonth): boolean {
  const { from, to } = trackingWindowOf(calendar);
  if (date < from) return false;
  if (date > to) return false;
  return true;
}

function monthOverlapsTrackingWindow(
  year: number,
  month: number,
  calendar: CalendarMonth,
): boolean {
  const { from, to } = trackingWindowOf(calendar);
  const monthStart = `${year}-${String(month).padStart(2, "0")}-01` as BusinessDate;
  let monthEnd = monthStart;
  while (Number(addDays(monthEnd, 1).slice(5, 7)) === month) {
    monthEnd = addDays(monthEnd, 1);
  }
  if (monthEnd < from) return false;
  if (monthStart > to) return false;
  return true;
}

function cycleAbsence(
  current: { type: AbsenceType | null; frac: AbsenceFraction },
  target: Exclude<AbsenceType, null>,
): { type: AbsenceType | null; frac: AbsenceFraction } {
  if (current.type !== target) return { type: target, frac: 1 };
  if (current.frac === 1) return { type: target, frac: 0.5 };
  return { type: null, frac: 0 };
}

function toDraft(day: CalendarDay, calendar: CalendarMonth, today: BusinessDate): DayDraft {
  const isFuture = day.entryDate > today;
  const isOutsideWindow = !isInTrackingWindow(day.entryDate as BusinessDate, calendar);
  const saved = day.saved;
  let totalHours = day.totalHours ?? 0;
  let rdHours = day.rdHours ?? 0;
  const preferred = day.isWorkday && calendar.workWeekdays.includes(isoWeekday(day.entryDate));
  const absenceFraction = (day.absenceFraction as AbsenceFraction) || 0;
  const absentElsewhere = isAbsentElsewhere(day);

  // Prefill only empty in-window days. Keep shared day_record totals when present so Gesamt
  // still shows cross-project hours even when this project has no time_entry yet.
  if (
    !saved &&
    !isFuture &&
    !isOutsideWindow &&
    !day.holiday &&
    preferred &&
    day.totalHours === null
  ) {
    // The inputs accept one decimal, so an unsaved prefill like 7.333 h is offered as 7.3.
    totalHours = round1(calendar.defaultDay);
    rdHours = 0;
  }

  // ACTUAL shows empty until the user enters a value; persisted / full-absence days keep 0 visible.
  const rdEntered = saved || day.rdHours !== null || absenceFraction === 1;

  return {
    entryDate: day.entryDate,
    ...hoursFromNumber(totalHours, rdHours, rdEntered),
    userTouched: saved,
    absenceType: day.absenceType,
    absenceFraction,
    preAbsenceSnapshot: null,
    absentElsewhere,
    absenceSourceProjectId: day.absenceSourceProjectId,
    absenceSourceProjectName: day.absenceSourceProjectName,
    hoursSourceProjects: day.hoursSourceProjects,
    clearOtherProjectHours: false,
    holiday: day.holiday,
    isWorkday: day.isWorkday,
    isFuture,
    isOutsideWindow,
    saved,
    isRetroactive: day.isRetroactive,
    isWorkdaySelected: seedWorkdaySelected(saved, calendar.workWeekdays, isoWeekday(day.entryDate)),
    antragRdShare: day.antragRdShare,
  };
}

export function TrackingCalendar({
  calendar,
  userId,
  userName,
  personLastName,
  asAdmin = false,
}: {
  calendar: CalendarMonth;
  userId: string;
  /** Greeting / hello display name (first name or full name). */
  userName?: string;
  /** Last name for `Stunden_{Project}_{LastName}_{year}.xlsx` filename. */
  personLastName?: string;
  asAdmin?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en-US" : "de-DE";
  const today = berlinToday();
  const todayYear = Number(today.slice(0, 4));
  const todayMonth = Number(today.slice(5, 7)); // 1–12
  const [month, setMonth] = useState(calendar.month - 1);
  /** Survives month-tab switches; dies with this component (modal close / navigation / refresh). */
  const [weekDraftCache, setWeekDraftCache] = useState<Record<string, CachedWeekDraft>>({});
  const draftScopeRef = useRef(`${userId}:${calendar.projectId}`);
  const logExport = useLogExport();
  const stundenzettel = useDownloadStundenzettel(calendar.projectId);

  // If this instance is reused for another person or project, drop drafts.
  useEffect(() => {
    const scope = `${userId}:${calendar.projectId}`;
    if (draftScopeRef.current === scope) return;
    draftScopeRef.current = scope;
    setWeekDraftCache({});
  }, [userId, calendar.projectId]);

  const monthsShort = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        new Date(2024, i, 1).toLocaleDateString(locale, { month: "short" }),
      ),
    [locale],
  );
  const monthName = new Date(calendar.year, month, 1).toLocaleDateString(locale, { month: "long" });

  const drafts = useMemo(
    () => calendar.days.map((day) => toDraft(day, calendar, today)),
    [calendar, today],
  );

  // Always show Mon–Fri in the selected month (Sat/Sun hidden). Profile work_weekdays only prefill.
  // Boundary weeks may be shorter (e.g. 3 days); the rest lives in the adjacent month tab.
  const weeks = useMemo(() => {
    const byWeek = new Map<string, { year: number; week: number; days: DayDraft[] }>();
    for (const day of drafts) {
      if (!day.isWorkday) continue;
      if (Number(day.entryDate.slice(0, 4)) !== calendar.year) continue;
      if (Number(day.entryDate.slice(5, 7)) !== month + 1) continue;
      const { year, week } = isoWeekOf(day.entryDate);
      const key = `${year}-${week}`;
      const bucket = byWeek.get(key) ?? { year, week, days: [] };
      bucket.days.push(day);
      byWeek.set(key, bucket);
    }
    return [...byWeek.values()].sort((a, b) => a.week - b.week || a.year - b.year);
  }, [drafts, month, calendar.year]);

  const weeksByMonth = useMemo(() => {
    return Array.from({ length: 12 }, (_unused, index) => {
      const monthDays = calendar.days.filter(
        (day) =>
          day.isWorkday &&
          Number(day.entryDate.slice(0, 4)) === calendar.year &&
          Number(day.entryDate.slice(5, 7)) === index + 1 &&
          isInTrackingWindow(day.entryDate as BusinessDate, calendar),
      );
      const byWeek = new Map<string, CalendarDay[]>();
      for (const day of monthDays) {
        const w = isoWeekOf(day.entryDate);
        const key = `${w.year}-${w.week}`;
        const bucket = byWeek.get(key) ?? [];
        bucket.push(day);
        byWeek.set(key, bucket);
      }
      let saved = 0;
      let total = 0;
      for (const days of byWeek.values()) {
        // Holidays/future do not count; align with WeekBlock savable candidates.
        const candidates = days.filter((day) => !day.holiday && day.entryDate <= today);
        const required = Math.min(calendar.workdaysPerWeek, candidates.length);
        if (required === 0) continue;
        total += 1;
        const filled = candidates.filter((day) => day.saved).length;
        if (filled >= required) saved += 1;
      }
      return { saved, total };
    });
  }, [calendar, today]);

  const totals = useMemo(() => {
    let totalH = 0;
    let totalRd = 0;
    let vac = 0;
    let sick = 0;
    for (const day of calendar.days) {
      if (Number(day.entryDate.slice(0, 4)) !== calendar.year) continue;
      if (Number(day.entryDate.slice(5, 7)) !== month + 1) continue;
      if (!isInTrackingWindow(day.entryDate as BusinessDate, calendar)) continue;
      if (!day.saved) continue;
      totalH += day.totalHours ?? 0;
      totalRd += day.rdHours ?? 0;
      if (day.absenceType === "vacation") vac += day.absenceFraction;
      if (day.absenceType === "sick") sick += day.absenceFraction;
    }
    return { totalH, totalRd, vac, sick };
  }, [calendar, month]);

  const viewMonthLocked = calendar.lockedMonths.some(
    (lock) => lock.year === calendar.year && lock.month === month + 1,
  );

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            {t("tracker.hoursHeading", { year: calendar.year })}
          </p>
          <h1 className="mt-1 text-3xl font-display">
            {t("tracker.hello", { name: userName ?? "" })}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("tracker.projectPrefix", { name: calendar.projectName })} ·{" "}
            {t("tracker.targetPerWeek", {
              weekly: calendar.weeklyHours,
              perDay: calendar.defaultDay.toFixed(1),
            })}{" "}
            ·{" "}
            <strong className="font-semibold text-foreground">
              {t("tracker.rdShare", { pct: formatPercent(calendar.rdShare).replace("%", "") })}
            </strong>
            {calendar.bundesland && ` · ${t("tracker.holidaysIn", { state: calendar.bundesland })}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="export-own-stundenzettel"
            disabled={stundenzettel.isPending}
            onClick={() =>
              stundenzettel.mutate(
                { year: calendar.year, userId },
                {
                  onSuccess: () => toast.success(t("projectLive.exportDone")),
                  onError: (error) => toast.error(describeError(error)),
                },
              )
            }
          >
            <FileSpreadsheet className="size-4" /> {t("projectLive.exportPerson")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              try {
                exportEmployeeYearMatrix(calendar, {
                  projectName: calendar.projectName,
                  lastName: personLastName || userName || calendar.userId,
                });
                void logExport.mutateAsync({
                  type: "export_year_matrix",
                  projectId: calendar.projectId,
                  year: calendar.year,
                  format: "xlsx",
                });
                toast.success(t("projectLive.exportDone"));
              } catch {
                toast.error(t("projectLive.exportFailed"));
              }
            }}
          >
            <Download className="size-4" /> {t("tracker.excelExport")}
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4 sm:max-w-2xl">
        <StatCard
          label={t("tracker.statTotal")}
          value={totals.totalH.toFixed(1)}
          unit={t("tracker.statUnitHours")}
        />
        <StatCard
          label={t("tracker.statRd")}
          value={totals.totalRd.toFixed(1)}
          unit={t("tracker.statUnitHours")}
        />
        <StatCard
          label={t("tracker.statVac")}
          value={totals.vac.toFixed(1)}
          unit={t("tracker.statUnitDays")}
        />
        <StatCard
          label={t("tracker.statSick")}
          value={totals.sick.toFixed(1)}
          unit={t("tracker.statUnitDays")}
        />
      </div>

      <div className="mt-2 flex flex-wrap gap-1 rounded-2xl border border-border bg-card p-1.5">
        {monthsShort.map((label, index) => {
          const active = index === month;
          const monthOutside = !monthOverlapsTrackingWindow(calendar.year, index + 1, calendar);
          const hours = calendar.days
            .filter(
              (day) =>
                Number(day.entryDate.slice(0, 4)) === calendar.year &&
                Number(day.entryDate.slice(5, 7)) === index + 1 &&
                day.saved &&
                isInTrackingWindow(day.entryDate as BusinessDate, calendar),
            )
            .reduce((sum, day) => sum + (day.totalHours ?? 0), 0);
          const completion = weeksByMonth[index]!;
          const isPast =
            calendar.year < todayYear || (calendar.year === todayYear && index + 1 < todayMonth);
          const monthLocked = calendar.lockedMonths.some(
            (lock) => lock.year === calendar.year && lock.month === index + 1,
          );
          // Never flag future or out-of-window months as missing.
          const missing = isPast && hours === 0 && !monthOutside && !monthLocked;
          // Selected locked months keep the normal selected (black) chrome; only
          // outside-window months stay muted when active.
          const base = active
            ? monthOutside
              ? "bg-muted text-muted-foreground"
              : "bg-foreground text-background"
            : monthOutside || monthLocked
              ? "text-muted-foreground/40"
              : missing
                ? "text-destructive hover:bg-destructive/10"
                : "text-muted-foreground hover:bg-muted";
          const monthTitle = monthOutside
            ? t("tracker.outsideWindowHint")
            : monthLocked
              ? t("monthLock.lockedHint")
              : undefined;
          const monthTestId = monthOutside
            ? `tracking-month-outside-${index + 1}`
            : monthLocked
              ? `tracking-month-locked-${index + 1}`
              : `tracking-month-tab-${index + 1}`;
          return (
            <button
              key={label}
              type="button"
              disabled={monthOutside}
              onClick={() => {
                if (!monthOutside) setMonth(index);
              }}
              className={`min-w-[64px] flex-1 rounded-xl px-3 py-2 text-sm transition ${base} ${
                monthOutside ? "cursor-not-allowed opacity-50" : ""
              }`}
              title={monthTitle}
              data-testid={monthTestId}
            >
              <div
                className={`flex items-center justify-center gap-1 font-medium ${
                  missing && !active && !monthLocked && !monthOutside ? "text-destructive" : ""
                }`}
              >
                {monthLocked && <Lock className="size-3 shrink-0" aria-hidden />}
                {label}
              </div>
              <div
                className={`text-[10px] tabular-nums ${
                  active
                    ? "opacity-80"
                    : missing && !monthLocked && !monthOutside
                      ? "text-destructive/80"
                      : ""
                }`}
              >
                {monthOutside
                  ? "—"
                  : hours > 0
                    ? `${hours.toFixed(0)}h`
                    : missing && !monthLocked
                      ? t("tracker.missing")
                      : "—"}
              </div>
              {monthOutside ? (
                <div
                  className={`text-[10px] ${active ? "opacity-80" : "text-muted-foreground/60"}`}
                >
                  {t("tracker.outsideWindowBadge")}
                </div>
              ) : monthLocked ? (
                <div
                  className={`text-[10px] ${active ? "opacity-80" : "text-muted-foreground/60"}`}
                >
                  {t("monthLock.lockedBadge")}
                </div>
              ) : (
                completion.total > 0 && (
                  <div className={`text-[10px] tabular-nums ${active ? "opacity-80" : ""}`}>
                    {completion.saved}/{completion.total} {t("tracker.weeksShort")}
                  </div>
                )
              )}
            </button>
          );
        })}
      </div>

      <h2 className="mt-8 flex flex-wrap items-center gap-2 text-2xl font-display">
        {viewMonthLocked && <Lock className="size-5 text-muted-foreground" aria-hidden />}
        <span className={viewMonthLocked ? "text-muted-foreground" : undefined}>
          {monthName} {calendar.year}
        </span>
        {viewMonthLocked && (
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
            {t("monthLock.lockedBadge")}
          </span>
        )}
      </h2>
      <p
        className="mt-1 text-sm text-muted-foreground"
        data-testid={viewMonthLocked ? "tracking-month-locked-body" : "tracking-month-hint"}
      >
        {viewMonthLocked ? t("monthLock.lockedMonthBody") : t("tracker.monthHint")}
      </p>

      <div className="mt-6 space-y-6">
        {weeks.map((week) => {
          const serverNote =
            calendar.weekNotes.find((n) => n.year === week.year && n.isoWeek === week.week)?.note ??
            "";
          // Remount when server data changes so drafts re-seed without a setState effect.
          const revision = weekDaysRevision(week.days);
          const cacheKey = weekDraftKey(userId, calendar.projectId, week.year, week.week);
          const cached = weekDraftCache[cacheKey];
          // Month-tab restore only. A newer server snapshot (other-project absence wipe,
          // shared Gesamt) must win over a draft seeded from stale React Query data.
          const useCache = cached !== undefined && cached.serverRevision === revision;
          return (
            <WeekBlock
              key={`${week.year}-${week.week}-${revision}-${serverNote}`}
              calendar={calendar}
              userId={userId}
              asAdmin={asAdmin}
              year={week.year}
              isoWeek={week.week}
              seedDays={useCache ? cached.rows : week.days}
              note={useCache ? cached.note : serverNote}
              onDraftChange={(snapshot) => {
                setWeekDraftCache((prev) => {
                  const existing = prev[cacheKey];
                  if (
                    existing &&
                    existing.note === snapshot.note &&
                    existing.rows === snapshot.rows &&
                    existing.serverRevision === revision
                  ) {
                    return prev;
                  }
                  return { ...prev, [cacheKey]: { ...snapshot, serverRevision: revision } };
                });
              }}
              onDraftClear={() => {
                setWeekDraftCache((prev) => {
                  if (!(cacheKey in prev)) return prev;
                  const next = { ...prev };
                  delete next[cacheKey];
                  return next;
                });
              }}
            />
          );
        })}
        {weeks.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            {t("tracker.noWorkdays")}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="tracking-stat-card">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-2xl font-display tabular-nums">{value}</span>
        {unit ? (
          <span className="text-xs font-medium text-muted-foreground tabular-nums">{unit}</span>
        ) : null}
      </div>
    </div>
  );
}

function WeekBlock({
  calendar,
  userId,
  asAdmin,
  year,
  isoWeek,
  seedDays,
  note: initialNote,
  onDraftChange,
  onDraftClear,
}: {
  calendar: CalendarMonth;
  userId: string;
  asAdmin: boolean;
  year: number;
  isoWeek: number;
  seedDays: DayDraft[];
  note: string;
  onDraftChange: (snapshot: WeekDraftSnapshot) => void;
  onDraftClear: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en-US" : "de-DE";
  const save = useSaveWeek({ userId, projectId: calendar.projectId, asAdmin });
  const attachmentsQuery = useWeekAttachments({
    userId,
    projectId: calendar.projectId,
    year,
    isoWeek,
    asAdmin,
  });
  const uploadAttachment = useUploadWeekAttachment({
    userId,
    projectId: calendar.projectId,
    year,
    isoWeek,
    asAdmin,
  });
  const deleteAttachment = useDeleteWeekAttachment({
    userId,
    projectId: calendar.projectId,
    year,
    isoWeek,
    asAdmin,
  });
  const downloadAttachment = useDownloadWeekAttachment({
    userId,
    projectId: calendar.projectId,
    asAdmin,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachments = attachmentsQuery.data ?? [];

  // seedDays is already scoped to this month's workdays — do not pad with other-month days.
  // May already be a cached draft when remounting after a month-tab switch.
  const [rows, setRows] = useState(() =>
    seedDays.map((row) =>
      row.totalHoursInput !== undefined && row.rdHoursInput !== undefined
        ? row
        : { ...row, ...hoursFromNumber(row.totalHours, row.rdHours, row.rdEntered) },
    ),
  );
  const [note, setNote] = useState(initialNote);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [changeReason, setChangeReason] = useState("");
  const [reasonTooShort, setReasonTooShort] = useState(false);
  /** Localized API failure shown inside the change-reason dialog (Ticket 99). */
  const [reasonSaveError, setReasonSaveError] = useState<string | null>(null);
  const [deleteReasonOpen, setDeleteReasonOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteReasonTooShort, setDeleteReasonTooShort] = useState(false);
  const [pendingAbsence, setPendingAbsence] = useState<{
    entryDate: BusinessDate;
    target: Exclude<AbsenceType, null>;
  } | null>(null);
  /** Suppress transient unparseable inline errors only while that cell is focused. */
  const [focusedInput, setFocusedInput] = useState<{
    entryDate: BusinessDate;
    field: Exclude<FocusedHoursField, null>;
  } | null>(null);
  const onDraftChangeRef = useRef(onDraftChange);

  useEffect(() => {
    onDraftChangeRef.current = onDraftChange;
  }, [onDraftChange]);

  useEffect(() => {
    onDraftChangeRef.current({ rows, note });
  }, [rows, note]);

  function isLocked(entryDate: BusinessDate): boolean {
    const m = Number(entryDate.slice(5, 7));
    const y = Number(entryDate.slice(0, 4));
    return calendar.lockedMonths.some((lock) => lock.year === y && lock.month === m);
  }

  const weekLocked = rows.some((row) => isLocked(row.entryDate));
  const weekLockMeta = rows
    .map((row) => {
      const m = Number(row.entryDate.slice(5, 7));
      const y = Number(row.entryDate.slice(0, 4));
      return calendar.lockedMonths.find((lock) => lock.year === y && lock.month === m);
    })
    .find((lock) => lock !== undefined);
  const weekOutside = rows.length > 0 && rows.every((row) => row.isOutsideWindow);
  const showWorkdayColumn = showsWorkdayColumn(calendar.workdaysPerWeek);
  const weekTotal = rows.reduce(
    (sum, row) => (showWorkdayColumn && !row.isWorkdaySelected ? sum : sum + row.totalHours),
    0,
  );
  // Show typed R&D totals (no silent min with Gesamt — invalid rows surface inline instead).
  const weekRd = rows.reduce(
    (sum, row) => (showWorkdayColumn && !row.isWorkdaySelected ? sum : sum + row.rdHours),
    0,
  );
  const weekVac = rows.reduce(
    (sum, row) =>
      showWorkdayColumn && !row.isWorkdaySelected
        ? sum
        : sum + (row.absenceType === "vacation" ? row.absenceFraction : 0),
    0,
  );
  const weekSick = rows.reduce(
    (sum, row) =>
      showWorkdayColumn && !row.isWorkdaySelected
        ? sum
        : sum + (row.absenceType === "sick" ? row.absenceFraction : 0),
    0,
  );
  const candidates = rows.filter(
    (row) => row.isWorkday && !row.holiday && !row.isFuture && !row.isOutsideWindow,
  );
  const savableRows = rows.filter((row) => !row.holiday && !row.isFuture && !row.isOutsideWindow);
  const isFutureOnlyWeek = savableRows.length === 0;
  // Completion still uses workdaysPerWeek (not checked days) so the calendar
  // badge stays aligned with reminder mail and the admin completion matrix.
  const requiredCount = Math.min(calendar.workdaysPerWeek, candidates.length);
  const savedCount = candidates.filter((row) => row.saved).length;
  const weekFullySaved = !weekOutside && requiredCount > 0 && savedCount >= requiredCount;
  const weekPartial = !weekOutside && savedCount > 0 && !weekFullySaved;
  const anySaved = !weekOutside && savedCount > 0;
  const attachmentsDisabled = weekLocked || weekOutside || isFutureOnlyWeek;
  const attachmentSlotsLeft = ATTACHMENT_MAX_FILES_PER_WEEK - attachments.length;

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  async function handleAttachmentSelect(event: ChangeEvent<HTMLInputElement>) {
    // FileList is live — copy before resetting the input, or the batch is empty.
    const picked = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!picked.length) return;

    const remaining = attachmentSlotsLeft;
    const batch = picked.slice(0, remaining);

    for (const file of batch) {
      const ext = extensionOf(file.name);
      if (!isAllowedExtension(ext)) {
        toast.error(t("errors.ATTACHMENT_TYPE_REJECTED"));
        continue;
      }
      if (file.size > ATTACHMENT_MAX_BYTES) {
        toast.error(t("errors.ATTACHMENT_TOO_LARGE"));
        continue;
      }
      try {
        await uploadAttachment.mutateAsync(file);
        toast.success(t("tracker.fileUploaded"));
      } catch (error) {
        toast.error(describeError(error));
      }
    }
  }

  function requestDeleteAttachment(attachmentId: string) {
    if (anySaved) {
      setPendingDeleteId(attachmentId);
      setDeleteReasonOpen(true);
      return;
    }
    void confirmDeleteAttachment(attachmentId);
  }

  async function confirmDeleteAttachment(attachmentId: string, reason?: string) {
    try {
      await deleteAttachment.mutateAsync({
        attachmentId,
        changeReason: reason,
      });
    } catch (error) {
      toast.error(describeError(error));
    }
  }

  function onConfirmDeleteReason() {
    const trimmed = deleteReason.trim();
    if (trimmed.length < 3) {
      setDeleteReasonTooShort(true);
      return;
    }
    if (!pendingDeleteId) return;
    void confirmDeleteAttachment(pendingDeleteId, trimmed);
    setDeleteReasonOpen(false);
    setPendingDeleteId(null);
    setDeleteReason("");
    setDeleteReasonTooShort(false);
  }

  function hoursCapFor(row: DayDraft): number {
    return row.absenceFraction === 0.5 ? calendar.dailyCap / 2 : calendar.dailyCap;
  }

  function validationFor(row: DayDraft): RowHoursValidation {
    return validateRow({
      totalHoursInput: row.totalHoursInput,
      rdHoursInput: row.rdHoursInput,
      rdEntered: row.rdEntered,
      hoursCap: hoursCapFor(row),
    });
  }

  /** Store the raw string with no coercion; derive numeric when parseable. */
  function setHoursInput(entryDate: BusinessDate, raw: string) {
    setRows((prev) =>
      prev.map((row) => {
        if (row.entryDate !== entryDate) return row;
        const parsed = parseHoursInput(raw);
        return {
          ...row,
          totalHoursInput: raw,
          totalHours: parsed ?? 0,
          userTouched: true,
        };
      }),
    );
  }

  function setRdInput(entryDate: BusinessDate, raw: string) {
    setRows((prev) =>
      prev.map((row) => {
        if (row.entryDate !== entryDate) return row;
        if (raw === "") {
          return {
            ...row,
            rdHoursInput: "",
            rdHours: 0,
            rdEntered: false,
            userTouched: true,
          };
        }
        const parsed = parseHoursInput(raw);
        return {
          ...row,
          rdHoursInput: raw,
          rdHours: parsed ?? 0,
          rdEntered: true,
          userTouched: true,
        };
      }),
    );
  }

  function blurHours(entryDate: BusinessDate) {
    setFocusedInput((prev) =>
      prev?.entryDate === entryDate && prev.field === "total" ? null : prev,
    );
    setRows((prev) =>
      prev.map((row) => {
        if (row.entryDate !== entryDate) return row;
        const normalized = normalizeHoursInput(row.totalHoursInput);
        if (normalized === null) return row;
        return {
          ...row,
          totalHours: normalized,
          totalHoursInput: formatHoursInput(normalized),
        };
      }),
    );
  }

  function blurRd(entryDate: BusinessDate) {
    setFocusedInput((prev) => (prev?.entryDate === entryDate && prev.field === "rd" ? null : prev));
    setRows((prev) =>
      prev.map((row) => {
        if (row.entryDate !== entryDate) return row;
        if (!row.rdEntered && row.rdHoursInput.trim() === "") return row;
        const normalized = normalizeHoursInput(row.rdHoursInput);
        if (normalized === null) return row;
        return {
          ...row,
          rdHours: normalized,
          rdHoursInput: formatHoursInput(normalized),
          rdEntered: true,
        };
      }),
    );
  }

  function applyAbsenceToggle(entryDate: BusinessDate, target: Exclude<AbsenceType, null>) {
    setRows((prev) =>
      prev.map((row) => {
        if (row.entryDate !== entryDate || row.holiday) return row;
        const next = cycleAbsence({ type: row.absenceType, frac: row.absenceFraction }, target);
        // Capture once when leaving "no absence"; type switches must keep the original snapshot.
        let preAbsenceSnapshot = row.preAbsenceSnapshot;
        if (row.absenceFraction === 0 && next.frac !== 0) {
          preAbsenceSnapshot = {
            totalHours: row.totalHours,
            rdHours: row.rdHours,
            totalHoursInput: row.totalHoursInput,
            rdHoursInput: row.rdHoursInput,
            rdEntered: row.rdEntered,
          };
        }
        let hours: ReturnType<typeof hoursFromNumber>;
        if (next.frac === 1) {
          hours = hoursFromNumber(0, 0, true);
        } else if (next.frac === 0.5) {
          const cap = round1(calendar.defaultDay / 2);
          const totalHours = Math.min(cap, row.totalHours || cap);
          // Leave R&D as typed — do not silently clamp to the new Gesamt.
          hours = hoursFromNumber(totalHours, row.rdHours, row.rdEntered);
        } else if (preAbsenceSnapshot) {
          // Cleared absence → restore exact pre-absence hours (full or half path).
          const snap = preAbsenceSnapshot;
          hours = {
            totalHours: snap.totalHours,
            rdHours: snap.rdHours,
            totalHoursInput: snap.totalHoursInput ?? formatHoursInput(snap.totalHours),
            rdHoursInput:
              snap.rdHoursInput ?? (snap.rdEntered ? formatHoursInput(snap.rdHours) : ""),
            rdEntered: snap.rdEntered,
          };
          preAbsenceSnapshot = null;
        } else {
          // No local snapshot (server-loaded absence / reload) → restore prefill; ACTUAL blank.
          hours = hoursFromNumber(round1(calendar.defaultDay), 0, false);
        }
        const clearOtherProjectHours =
          next.frac === 1 && row.hoursSourceProjects.length > 0
            ? true
            : next.frac === 1
              ? row.clearOtherProjectHours
              : false;
        return {
          ...row,
          absenceType: next.type,
          absenceFraction: next.frac,
          ...hours,
          preAbsenceSnapshot,
          clearOtherProjectHours,
          userTouched: true,
        };
      }),
    );
  }

  function toggleAbsence(entryDate: BusinessDate, target: Exclude<AbsenceType, null>) {
    const row = rows.find((candidate) => candidate.entryDate === entryDate);
    if (!row || row.holiday) return;
    const next = cycleAbsence({ type: row.absenceType, frac: row.absenceFraction }, target);
    if (next.frac === 1 && row.hoursSourceProjects.length > 0 && row.absenceFraction !== 1) {
      setPendingAbsence({ entryDate, target });
      return;
    }
    applyAbsenceToggle(entryDate, target);
  }

  function confirmPendingAbsence() {
    if (!pendingAbsence) return;
    applyAbsenceToggle(pendingAbsence.entryDate, pendingAbsence.target);
    setPendingAbsence(null);
  }

  async function commit(reason: string | null) {
    // Holidays and future days are display-only (reference calendar skips them on save).
    // Parse string drafts → JSON numbers (weekDaySaveSchema rejects strings).
    const days: WeekDaySaveInput[] = [];
    for (const row of rows) {
      if (!isWorkdayRowSavable(row, showWorkdayColumn)) continue;
      const totalHours = normalizeHoursInput(row.totalHoursInput);
      const rdHours = row.rdEntered
        ? normalizeHoursInput(row.rdHoursInput)
        : normalizeHoursInput(row.rdHoursInput === "" ? "0" : row.rdHoursInput);
      if (totalHours === null || rdHours === null) {
        toast.error(t("tracker.weekHoursInvalid", { dates: row.entryDate }));
        return;
      }
      days.push({
        entryDate: row.entryDate,
        totalHours,
        rdHours,
        absenceType: row.absenceType,
        absenceFraction: row.absenceFraction,
        ...(row.clearOtherProjectHours ? { clearOtherProjectHours: true } : {}),
      });
    }

    try {
      await save.mutateAsync({
        year,
        isoWeek,
        days,
        note,
        changeReason: reason,
      });
      // Drop the temp draft so the post-save calendar refetch reseeds from the server.
      onDraftClear();
      setReasonOpen(false);
      setChangeReason("");
      setReasonTooShort(false);
      setReasonSaveError(null);
      toast.success(t("tracker.weekSavedToast", { kw: isoWeek }));
    } catch (error) {
      // Keep the change-reason dialog open on API failure; toast + modal share the same copy.
      setReasonTooShort(false);
      let message: string;
      let toastTestId = "tracking-save-error-toast";
      if (error instanceof ApiError && error.code === "CROSS_PROJECT_OVERBOOKING") {
        message = formatCrossProjectOverbookingMessage(
          t,
          error.payload as CrossProjectOverbookingPayload,
        );
        toastTestId = "tracking-overbook-toast-body";
      } else if (
        error instanceof ApiError &&
        error.code === "ABSENCE_BLOCKED_BY_OTHER_PROJECT_HOURS"
      ) {
        message = formatAbsenceBlockedMessage(
          t,
          error,
          error.payload as AbsenceBlockedByOtherProjectHoursPayload,
        );
        toastTestId = "tracking-absence-blocked-toast-body";
      } else {
        message = describeError(error);
      }
      if (reason !== null) {
        setReasonSaveError(message);
      }
      toastMultilineError(message, toastTestId);
    }
  }

  function onSave() {
    if (weekLocked) {
      const lockedDay = rows.find((row) => isLocked(row.entryDate));
      const month = lockedDay ? Number(lockedDay.entryDate.slice(5, 7)) : calendar.month;
      const year = lockedDay ? Number(lockedDay.entryDate.slice(0, 4)) : calendar.year;
      const lockMeta = calendar.lockedMonths.find(
        (lock) => lock.year === year && lock.month === month,
      );
      toast.error(
        t("errors.MONTH_LOCKED", {
          month,
          year,
          project_name: lockMeta?.projectName ?? "",
        }),
      );
      return;
    }

    const savable = rows.filter((row) => isWorkdayRowSavable(row, showWorkdayColumn));

    // Prefill alone is not enough — user must touch at least one day.
    if (!savable.some((row) => row.userTouched)) {
      toast.error(t("tracker.weekNothingEntered"));
      return;
    }

    // Entire week of zeros (no hours, no absence) is not a valid save.
    const allZero = savable.every(
      (row) => row.totalHours === 0 && row.rdHours === 0 && row.absenceFraction === 0,
    );
    if (allZero) {
      toast.error(t("tracker.weekAllZero"));
      return;
    }

    // Any extra checked weekday needs Gesamt hours or an absence (F&E may be 0).
    if (
      savable.some((row) =>
        extraCheckedDayMissingHours(
          row,
          showWorkdayColumn,
          calendar.workWeekdays,
          isoWeekday(row.entryDate),
        ),
      )
    ) {
      toast.error(t("tracker.workdayExtraNeedsHours"));
      return;
    }

    // Any single day at 0/0 without an absence reason is not a valid save.
    if (
      savable.some(
        (row) =>
          row.totalHours === 0 &&
          row.rdHours === 0 &&
          row.absenceType === null &&
          row.absenceFraction === 0,
      )
    ) {
      toast.error(t("tracker.weekZeroHoursRequireAbsence"));
      return;
    }

    // Blank ACTUAL must not silently become 0 — user must enter a value on every day.
    if (savable.some((row) => !row.rdEntered)) {
      toast.error(t("tracker.weekActualRequired"));
      return;
    }

    const invalidDates = savable
      .filter((row) => rowHasHoursError(validationFor(row)))
      .map((row) => row.entryDate);
    if (invalidDates.length > 0) {
      toast.error(t("tracker.weekHoursInvalid", { dates: invalidDates.join(", ") }));
      return;
    }

    // Reference anySaved rule: first-time week save is frictionless; re-save asks for a reason.
    // Ticket 116: wiping other projects' hours also needs a reason even when this project
    // has no saved day yet (anySaved is project-scoped).
    if (anySaved || savable.some((row) => row.clearOtherProjectHours)) {
      setChangeReason("");
      setReasonTooShort(false);
      setReasonSaveError(null);
      setReasonOpen(true);
      return;
    }

    void commit(null);
  }

  function onConfirmReason() {
    const trimmed = changeReason.trim();
    if (trimmed.length < 3) {
      setReasonTooShort(true);
      return;
    }
    void commit(trimmed);
  }

  function workdayLockOf(row: DayDraft): WorkdayCheckboxLockReason | null {
    return workdayCheckboxLockReason(
      {
        holiday: row.holiday,
        isFuture: row.isFuture,
        isOutsideWindow: row.isOutsideWindow,
        absentElsewhere: row.absentElsewhere,
        hoursSourceProjectNames: row.hoursSourceProjects.map((project) => project.name),
        saved: row.saved,
      },
      isLocked(row.entryDate),
      calendar.workWeekdays,
      isoWeekday(row.entryDate),
    );
  }

  function setWorkdaySelected(entryDate: BusinessDate, selected: boolean) {
    setRows((prev) =>
      prev.map((row) => {
        if (row.entryDate !== entryDate) return row;
        if (workdayLockOf(row)) return row;
        if (!selected) {
          return {
            ...row,
            isWorkdaySelected: false,
            ...hoursFromNumber(0, 0, false),
            userTouched: true,
          };
        }
        return { ...row, isWorkdaySelected: true, userTouched: true };
      }),
    );
  }

  function workdayLockTitle(reason: WorkdayCheckboxLockReason | null): string | undefined {
    if (!reason) return undefined;
    const keys: Record<WorkdayCheckboxLockReason, string> = {
      holiday: "tracker.workdayLockedHoliday",
      future: "tracker.workdayLockedFuture",
      outsideWindow: "tracker.workdayLockedOutside",
      locked: "tracker.workdayLockedMonth",
      absentElsewhere: "tracker.workdayLockedElsewhere",
      otherProjectHours: "tracker.workdayLockedOtherProject",
      assignedWorkday: "tracker.workdayLockedAssigned",
      hasData: "tracker.workdayLockedHasData",
    };
    return t(keys[reason]);
  }

  const pendingAbsenceRow = pendingAbsence
    ? rows.find((row) => row.entryDate === pendingAbsence.entryDate)
    : undefined;
  const pendingAbsenceProjects = pendingAbsenceRow
    ? [
        calendar.projectName,
        ...pendingAbsenceRow.hoursSourceProjects.map((project) => project.name),
      ]
        .filter((name, index, all) => name.length > 0 && all.indexOf(name) === index)
        .join(", ")
    : "";

  return (
    <div
      className={
        weekLocked || weekOutside
          ? "rounded-2xl border border-border bg-muted/40 text-muted-foreground"
          : "rounded-2xl border border-border bg-card"
      }
      data-testid={`tracking-week-${year}-${isoWeek}`}
      data-locked={weekLocked ? "true" : undefined}
      data-outside={weekOutside ? "true" : undefined}
      title={weekOutside ? t("tracker.outsideWindowHint") : undefined}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3
            className={`flex items-center gap-1.5 text-lg font-display ${
              weekLocked || weekOutside ? "text-muted-foreground" : ""
            }`}
          >
            {weekLocked && <Lock className="size-4 shrink-0" aria-hidden />}
            {t("tracker.week", { kw: isoWeek })}
          </h3>
          {weekOutside && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
              {t("tracker.outsideWindowBadge")}
            </span>
          )}
          {weekLocked && !weekOutside && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
              <Lock className="size-3" aria-hidden /> {t("monthLock.lockedBadge")}
              {weekLockMeta?.projectName ? ` · ${weekLockMeta.projectName}` : ""}
            </span>
          )}
          {!weekLocked && !weekOutside && weekFullySaved && (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-emerald-700">
              <Check className="size-3" /> {t("tracker.weekSaved")}
            </span>
          )}
          {!weekLocked && !weekOutside && weekPartial && (
            <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-amber-800">
              {t("tracker.weekPartial")}
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground tabular-nums">
          Σ {weekTotal.toFixed(1)}h · FuE {weekRd.toFixed(1)}h{weekVac > 0 && <> · 🌴 {weekVac}</>}
          {weekSick > 0 && <> · 🤒 {weekSick}</>}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table
          className={`w-full min-w-[760px] text-sm ${weekLocked || weekOutside ? "opacity-70" : ""}`}
        >
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
              {showWorkdayColumn ? (
                <th className="px-3 py-2 font-medium" data-testid="tracking-workday-col">
                  {t("tracker.colWorkday")}
                </th>
              ) : null}
              <th className="px-5 py-2 font-medium">{t("tracker.colDay")}</th>
              <th className="px-3 py-2 text-right font-medium">{t("tracker.colHours")}</th>
              <th className="px-3 py-2 text-right font-medium">{t("tracker.colRdPlanned")}</th>
              <th className="px-3 py-2 text-right font-medium">{t("tracker.colRdActual")}</th>
              <th className="px-3 py-2 text-center font-medium">{t("tracker.colAbsence")}</th>
              <th className="px-5 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const weekday = formatWeekdayShort(row.entryDate, locale);
              const antrag = plannedRd(row.totalHours, row.antragRdShare);
              const isHoliday = Boolean(row.holiday);
              const workdayOff = showWorkdayColumn && !row.isWorkdaySelected;
              const disabled =
                isHoliday ||
                row.isFuture ||
                row.isOutsideWindow ||
                isLocked(row.entryDate) ||
                row.absentElsewhere ||
                workdayOff;
              const checkboxLock = workdayLockOf(row);
              const fullAbsent = row.absenceFraction === 1;
              const hoursCap = hoursCapFor(row);
              const plannedValue = disabled || fullAbsent ? 0 : antrag;
              const rowBg = disabled ? "bg-muted/40" : fullAbsent ? "bg-muted/20" : "";
              const focusedField: FocusedHoursField =
                focusedInput?.entryDate === row.entryDate ? focusedInput.field : null;
              const validation =
                disabled || fullAbsent
                  ? null
                  : suppressFocusedUnparseable(validationFor(row), focusedField);
              const totalErrorMsg = validation?.totalInvalid
                ? validation.totalIssues.includes("overCap")
                  ? t("tracker.hoursOverCap", { cap: hoursCap })
                  : t("tracker.hoursInvalidNumber")
                : null;
              const rdErrorMsg = validation?.rdInvalid
                ? validation.rdIssues.includes("rdGtTotal")
                  ? t("tracker.rdOverTotal")
                  : t("tracker.hoursInvalidNumber")
                : null;

              // Full absence recorded on another project: hatch the whole row and put the legend on it.
              if (row.absentElsewhere) {
                const sourceId = row.absenceSourceProjectId;
                const sourceName = row.absenceSourceProjectName;
                return (
                  <tr
                    key={row.entryDate}
                    className="border-t border-border/60"
                    data-testid={`tracking-absent-elsewhere-${row.entryDate}`}
                  >
                    <td colSpan={showWorkdayColumn ? 7 : 6} className="p-0">
                      <div
                        className="relative flex min-h-14 items-center justify-center px-5 py-3 bg-muted/60"
                        style={{
                          // Explicit gray X-hatch so the pattern stays visible on any theme.
                          backgroundImage: [
                            "repeating-linear-gradient(45deg, transparent 0 7px, rgba(120,120,120,0.55) 7px 8px)",
                            "repeating-linear-gradient(-45deg, transparent 0 7px, rgba(120,120,120,0.55) 7px 8px)",
                          ].join(", "),
                        }}
                      >
                        <div
                          className="relative z-10 max-w-xl rounded-md bg-background/90 px-3 py-1.5 text-center text-sm text-muted-foreground shadow-sm"
                          data-testid="tracking-absent-elsewhere-legend"
                        >
                          <span className="mr-2 font-medium text-foreground">
                            {weekday} {row.entryDate.slice(8)}
                          </span>
                          {t("tracker.absentElsewhere")}
                          {sourceId && sourceName ? (
                            <>
                              {" "}
                              <a
                                href={hoursSourceProjectHref(sourceId, asAdmin)}
                                className="font-medium text-foreground underline underline-offset-2"
                                data-testid="tracking-absent-elsewhere-link"
                              >
                                {sourceName}
                              </a>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              }

              return (
                <tr
                  key={row.entryDate}
                  className={`border-t border-border/60 ${rowBg}`}
                  data-testid={
                    isHoliday
                      ? `tracking-holiday-${row.entryDate}`
                      : `tracking-day-${row.entryDate}`
                  }
                >
                  {showWorkdayColumn ? (
                    <td className="align-top px-3 py-2">
                      <div className="flex min-h-9 items-center">
                        <Checkbox
                          checked={row.isWorkdaySelected}
                          disabled={Boolean(checkboxLock)}
                          title={workdayLockTitle(checkboxLock)}
                          aria-label={t("tracker.colWorkday")}
                          data-testid={`tracking-workday-${row.entryDate}`}
                          onCheckedChange={(value) =>
                            setWorkdaySelected(row.entryDate, value === true)
                          }
                        />
                      </div>
                    </td>
                  ) : null}
                  <td className="align-top px-5 py-2">
                    <div className="flex min-h-9 items-center font-medium">
                      {weekday} {row.entryDate.slice(8)}
                      {isHoliday && (
                        <span
                          className="ml-2 rounded-full bg-accent/40 px-2 py-0.5 text-[10px] font-normal"
                          data-testid={`tracking-holiday-badge-${row.entryDate}`}
                        >
                          {row.holiday}
                        </span>
                      )}
                      {row.isFuture && (
                        <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[10px] font-normal text-muted-foreground">
                          {t("tracker.future")}
                        </span>
                      )}
                      {row.isOutsideWindow && !row.isFuture && (
                        <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[10px] font-normal text-muted-foreground">
                          {t("tracker.outsideWindowBadge")}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="align-top px-3 py-2 text-right">
                    <div className="ml-auto flex flex-col items-end">
                      <div className="flex items-center justify-end gap-1">
                        {row.hoursSourceProjects.length > 0 ? (
                          <HoursSourceHint projects={row.hoursSourceProjects} asAdmin={asAdmin} />
                        ) : null}
                        <Input
                          type="number"
                          min={0}
                          max={hoursCap}
                          step={0.5}
                          className={`w-24 tabular-nums ${
                            totalErrorMsg ? "border-destructive focus-visible:ring-destructive" : ""
                          }`}
                          value={row.totalHoursInput}
                          disabled={disabled || fullAbsent}
                          aria-invalid={totalErrorMsg ? true : undefined}
                          data-testid={`tracking-hours-${row.entryDate}`}
                          onFocus={() =>
                            setFocusedInput({ entryDate: row.entryDate, field: "total" })
                          }
                          onChange={(event) => setHoursInput(row.entryDate, event.target.value)}
                          onBlur={() => blurHours(row.entryDate)}
                        />
                      </div>
                      {totalErrorMsg ? (
                        <p
                          className="mt-1 w-24 text-left text-xs leading-snug break-words text-destructive"
                          data-testid={`tracking-hours-error-${row.entryDate}`}
                        >
                          {totalErrorMsg}
                        </p>
                      ) : null}
                    </div>
                  </td>
                  <td
                    className="align-top px-3 py-2 text-right"
                    data-testid={`tracking-antrag-${row.entryDate}`}
                  >
                    <Input
                      type="number"
                      readOnly
                      tabIndex={-1}
                      disabled={disabled || fullAbsent}
                      className="ml-auto w-24 tabular-nums bg-muted/60 text-muted-foreground"
                      value={plannedValue.toFixed(1)}
                    />
                  </td>
                  <td className="align-top px-3 py-2 text-right">
                    <div className="ml-auto flex w-24 flex-col items-stretch">
                      <Input
                        type="number"
                        min={0}
                        max={row.totalHours}
                        step={0.5}
                        className={`w-24 tabular-nums ${
                          rdErrorMsg ? "border-destructive focus-visible:ring-destructive" : ""
                        }`}
                        value={row.rdHoursInput}
                        disabled={disabled || fullAbsent}
                        aria-invalid={rdErrorMsg ? true : undefined}
                        data-testid={`tracking-rd-${row.entryDate}`}
                        onFocus={() => setFocusedInput({ entryDate: row.entryDate, field: "rd" })}
                        onChange={(event) => setRdInput(row.entryDate, event.target.value)}
                        onBlur={() => blurRd(row.entryDate)}
                      />
                      {rdErrorMsg ? (
                        <p
                          className="mt-1 w-full text-left text-xs leading-snug break-words text-destructive"
                          data-testid={`tracking-rd-error-${row.entryDate}`}
                        >
                          {rdErrorMsg}
                        </p>
                      ) : null}
                    </div>
                  </td>
                  <td className="align-top px-3 py-2">
                    <div className="flex min-h-9 items-center justify-center gap-1.5">
                      {(
                        [
                          ["vacation", Palmtree, t("tracker.vacation")],
                          ["sick", Thermometer, t("tracker.sick")],
                          ["other", HelpCircle, t("tracker.other")],
                        ] as const
                      ).map(([key, Icon, label]) => (
                        <AbsenceChip
                          key={key}
                          icon={<Icon className="size-3.5" />}
                          label={label}
                          active={row.absenceType === key}
                          frac={row.absenceType === key ? row.absenceFraction : 0}
                          disabled={disabled}
                          testId={`tracking-absence-${row.entryDate}-${key}`}
                          onClick={() => toggleAbsence(row.entryDate, key)}
                        />
                      ))}
                    </div>
                  </td>
                  <td className="align-top px-5 py-2 text-right text-xs text-muted-foreground">
                    <div className="flex min-h-9 items-center justify-end">
                      {row.saved && !isHoliday && !row.isOutsideWindow ? (
                        <Check className="inline size-3.5 text-foreground/60" />
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 border-t border-border px-5 py-4">
        <div>
          <Label htmlFor={`note-${year}-${isoWeek}`}>{t("tracker.descLabel")}</Label>
          <Textarea
            id={`note-${year}-${isoWeek}`}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t("tracker.descPlaceholder")}
            disabled={weekLocked || weekOutside || isFutureOnlyWeek}
            className="mt-1.5 min-h-[70px]"
            data-testid={`tracking-note-${year}-${isoWeek}`}
          />
        </div>

        <div data-testid="week-attachments">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label htmlFor={`attach-${year}-${isoWeek}`}>{t("tracker.attachFile")}</Label>
            <span className="text-xs text-muted-foreground">
              {t("tracker.attachmentsCount", {
                count: attachments.length,
                max: ATTACHMENT_MAX_FILES_PER_WEEK,
              })}
            </span>
          </div>
          <input
            id={`attach-${year}-${isoWeek}`}
            ref={fileInputRef}
            type="file"
            multiple
            accept={allowedExtensionsAccept()}
            className="sr-only"
            data-testid="week-attach-input"
            disabled={attachmentsDisabled || attachmentSlotsLeft <= 0}
            onChange={(event) => void handleAttachmentSelect(event)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            data-testid="week-attach-add"
            disabled={attachmentsDisabled || attachmentSlotsLeft <= 0 || uploadAttachment.isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="size-4" />
            {uploadAttachment.isPending ? t("tracker.uploading") : t("tracker.attachFile")}
          </Button>
          {attachments.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">{t("tracker.attachmentsEmpty")}</p>
          ) : (
            <ul className="mt-2 space-y-1">
              {attachments.map((file) => (
                <li
                  key={file.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-border/60 px-2 py-1.5 text-sm"
                  data-testid="week-attachment-row"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{file.filename}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatFileSize(file.byteSize)}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      aria-label={t("tracker.attachmentDownload")}
                      onClick={() => void downloadAttachment.mutateAsync(file.id)}
                    >
                      <Download className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive"
                      aria-label={t("tracker.attachmentDelete")}
                      disabled={attachmentsDisabled || deleteAttachment.isPending}
                      onClick={() => requestDeleteAttachment(file.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end border-t border-border px-5 py-3">
        <Button
          size="sm"
          onClick={onSave}
          disabled={save.isPending || weekLocked || weekOutside || isFutureOnlyWeek}
          title={
            weekOutside
              ? t("tracker.outsideWindowHint")
              : isFutureOnlyWeek
                ? t("tracker.futureWeekNotSaveable")
                : undefined
          }
          data-testid={`tracking-save-${year}-${isoWeek}`}
        >
          {save.isPending ? (
            t("common.saving")
          ) : (
            <>
              <Save className="size-4" />{" "}
              {anySaved ? t("tracker.saveAgain") : t("tracker.saveWeek")}
            </>
          )}
        </Button>
      </div>

      <ConfirmDialog
        open={pendingAbsence !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAbsence(null);
        }}
        title={t("tracker.absenceClearsOtherProjectsTitle")}
        body={t("tracker.absenceClearsOtherProjectsBody", { projects: pendingAbsenceProjects })}
        confirmLabel={t("tracker.absenceClearsOtherProjectsConfirm")}
        destructive
        testId="tracking-absence-cross-project-dialog"
        confirmTestId="tracking-absence-cross-project-confirm"
        cancelTestId="tracking-absence-cross-project-cancel"
        onConfirm={confirmPendingAbsence}
      />

      <Dialog
        open={reasonOpen}
        onOpenChange={(open) => {
          setReasonOpen(open);
          if (!open) {
            setChangeReason("");
            setReasonTooShort(false);
            setReasonSaveError(null);
          }
        }}
      >
        <DialogContent data-testid="tracking-reason-dialog">
          <DialogHeader>
            <DialogTitle>{t("tracker.reasonTitle")}</DialogTitle>
            <DialogDescription>{t("tracker.reasonBody")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`tracking-reason-${year}-${isoWeek}`}>{t("tracker.reasonTitle")}</Label>
            <Textarea
              id={`tracking-reason-${year}-${isoWeek}`}
              value={changeReason}
              onChange={(event) => {
                setChangeReason(event.target.value);
                if (reasonTooShort) setReasonTooShort(false);
                if (reasonSaveError) setReasonSaveError(null);
              }}
              placeholder={t("tracker.reasonPlaceholder")}
              data-testid="tracking-reason"
            />
            {reasonTooShort ? (
              <p className="text-sm text-destructive" data-testid="tracking-reason-error">
                {t("tracker.reasonTooShort")}
              </p>
            ) : null}
            {reasonSaveError ? (
              <p
                className="whitespace-pre-line text-sm text-destructive"
                data-testid="tracking-reason-save-error"
              >
                {reasonSaveError}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReasonOpen(false)}
              data-testid="tracking-reason-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={onConfirmReason}
              disabled={save.isPending}
              data-testid="tracking-reason-confirm"
            >
              {save.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteReasonOpen}
        onOpenChange={(open) => {
          setDeleteReasonOpen(open);
          if (!open) {
            setPendingDeleteId(null);
            setDeleteReason("");
            setDeleteReasonTooShort(false);
          }
        }}
      >
        <DialogContent data-testid="tracking-attachment-delete-reason-dialog">
          <DialogHeader>
            <DialogTitle>{t("tracker.reasonTitle")}</DialogTitle>
            <DialogDescription>{t("tracker.reasonBody")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`tracking-delete-reason-${year}-${isoWeek}`}>
              {t("tracker.reasonTitle")}
            </Label>
            <Textarea
              id={`tracking-delete-reason-${year}-${isoWeek}`}
              value={deleteReason}
              onChange={(event) => {
                setDeleteReason(event.target.value);
                if (deleteReasonTooShort) setDeleteReasonTooShort(false);
              }}
              className="min-h-[70px]"
            />
            {deleteReasonTooShort ? (
              <p className="text-xs text-destructive">{t("errors.CHANGE_REASON_REQUIRED")}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteReasonOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={onConfirmDeleteReason} disabled={deleteAttachment.isPending}>
              {deleteAttachment.isPending ? t("common.saving") : t("tracker.attachmentDelete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AbsenceChip({
  icon,
  active,
  frac,
  label,
  disabled,
  testId,
  onClick,
}: {
  icon: ReactNode;
  active: boolean;
  frac: AbsenceFraction;
  label: string;
  disabled: boolean;
  testId: string;
  onClick: () => void;
}) {
  const badge = active ? (frac === 0.5 ? "½" : "1") : "";
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      title={label}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] transition ${
        active
          ? "border-foreground bg-foreground text-background"
          : "border-border text-muted-foreground hover:bg-muted"
      } ${disabled ? "cursor-not-allowed opacity-40" : ""}`}
    >
      {icon}
      <span>{label}</span>
      {badge ? <span className="ml-0.5 font-medium tabular-nums">{badge}</span> : null}
    </button>
  );
}
